<template>
  <button
    v-if="isVisible"
    @click="scrollToTop"
    :class="['zs-scrolltop', positionClass]"
    :style="buttonStyle"
  >
    <ZSIcon :icon="icon" flip-v :size="iconSize" :color="iconColor"></ZSIcon>
  </button>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import ZSIcon from "./global/ZSIcon.vue";

const props = defineProps({
  threshold: {
    type: Number,
    default: 300,
  },
  target: {
    type: String,
    default: 'window', // 'window' or 'parent'
  },
  icon: {
    type: String,
    default: 'Chevron',
  },
  iconSize: {
    type: String,
    default: 'large',
  },
  iconColor: {
    type: String,
    default: 'white',
  },
  position: {
    type: String,
    default: 'bottom-right', // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
  },
  bottom: {
    type: String,
    default: '20px',
  },
  right: {
    type: String,
    default: '20px',
  },
  left: {
    type: String,
    default: '20px',
  },
  top: {
    type: String,
    default: '20px',
  },
});

const isVisible = ref(false);

const scrollHandler = () => {
  let scrollTop = 0;
  if (props.target === 'window') {
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  } else if (props.target === 'parent') {
    const parent = document.querySelector('.zs-page'); // Assuming .zs-page is the scrollable parent
    if (parent) {
      scrollTop = parent.scrollTop;
    }
  }
  isVisible.value = scrollTop > props.threshold;
};

const scrollToTop = () => {
  if (props.target === 'window') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else if (props.target === 'parent') {
    const parent = document.querySelector('.zs-page');
    if (parent) {
      parent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};

onMounted(() => {
  const scrollElement = props.target === 'window' ? window : document.querySelector('.zs-page');
  if (scrollElement) {
    scrollElement.addEventListener('scroll', scrollHandler);
    scrollHandler(); // Initial check
  }
});

onBeforeUnmount(() => {
  const scrollElement = props.target === 'window' ? window : document.querySelector('.zs-page');
  if (scrollElement) {
    scrollElement.removeEventListener('scroll', scrollHandler);
  }
});

const positionClass = computed(() => {
  return `zs-scrolltop-${props.position}`;
});

const buttonStyle = computed(() => {
  const style = {};
  if (props.position.includes('bottom')) {
    style.bottom = props.bottom;
  }
  if (props.position.includes('top')) {
    style.top = props.top;
  }
  if (props.position.includes('right')) {
    style.right = props.right;
  }
  if (props.position.includes('left')) {
    style.left = props.left;
  }
  return style;
});
</script>

<style scoped>
.zs-scrolltop {
  position: fixed;
  background-color: var(--color-orange, #e84e0f); /* Use a CSS variable or default */
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: opacity 0.3s, transform 0.3s;
  opacity: 0.8;
  z-index: 1000;
}

.zs-scrolltop:hover {
  opacity: 1;
  transform: scale(1.1);
}

.zs-scrolltop-bottom-right {
  bottom: var(--bottom);
  right: var(--right);
}

.zs-scrolltop-bottom-left {
  bottom: var(--bottom);
  left: var(--left);
}

.zs-scrolltop-top-right {
  top: var(--top);
  right: var(--right);
}

.zs-scrolltop-top-left {
  top: var(--top);
  left: var(--left);
}
</style>
